import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Shield,
  Activity,
  Building2,
  Mail,
  HardDrive,
  AlertTriangle,
  FileText,
  Settings,
  LogOut,
  ExternalLink,
  ChevronRight,
} from "lucide-react";
import { useSuperAdmin } from "./SuperAdminContext";
import { toast } from "sonner";
import SignOutConfirmModal from "../../components/SignOutConfirmModal";

export default function SuperAdminLayout({
  activeTab,
  onSelectTab,
  children,
}) {
  const { admin, logout } = useSuperAdmin();
  const navigate = useNavigate();

  const [signOutModalOpen, setSignOutModalOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleLogout = () => {
    setSignOutModalOpen(true);
  };

  const handleConfirmLogout = async () => {
    try {
      setSigningOut(true);
      await logout();
      toast.success("Super Admin signed out");
      setSignOutModalOpen(false);
      navigate("/super-admin/login");
    } catch (err) {
      toast.error("Failed to sign out. Please try again.");
    } finally {
      setSigningOut(false);
    }
  };

  const navItems = [
    { id: "overview", label: "Overview", icon: Activity },
    { id: "companies", label: "Companies", icon: Building2 },
    { id: "smtp", label: "Email Server", icon: Mail },
    { id: "health", label: "System Health", icon: HardDrive },
    { id: "errors", label: "Error Logs", icon: AlertTriangle },
    { id: "audit", label: "Security Audit", icon: FileText },
    { id: "settings", label: "Platform & Backup", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 flex flex-col font-sans selection:bg-teal-500 selection:text-white">
      {/* Top Mission Control Bar */}
      <header className="sticky top-0 z-40 border-b border-slate-800/80 bg-[#0E1526]/85 backdrop-blur-xl px-4 sm:px-8 py-3.5 flex items-center justify-between shadow-2xl">
        {/* Brand & Badge */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-teal-500 to-emerald-400 flex items-center justify-center shadow-lg shadow-teal-500/25">
            <Shield className="w-5 h-5 text-slate-950 font-bold" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-extrabold tracking-[0.25em] text-teal-400 uppercase">
                Rental Tracker
              </span>
              <span className="px-1.5 py-0.2 rounded text-[9px] font-black uppercase tracking-wider bg-teal-500/15 text-teal-300 border border-teal-500/30">
                Super Admin
              </span>
            </div>
            <div className="text-sm font-bold tracking-tight text-white flex items-center gap-1.5">
              <span>Mission Control Console</span>
            </div>
          </div>
        </div>

        {/* Right Status & Controls */}
        <div className="flex items-center gap-3">
          <Link
            to="/"
            target="_blank"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-700/80 text-xs font-semibold text-slate-300 hover:text-white hover:border-slate-600 transition-colors shadow-sm"
          >
            <span>Tenant App</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </Link>

          {/* Admin User Info */}
          <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl bg-slate-900/80 border border-slate-800">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <div className="text-xs">
              <span className="text-slate-400 text-[11px] hidden sm:inline">User: </span>
              <span className="text-white font-semibold">{admin?.username || "root"}</span>
            </div>
          </div>

          {/* Sign Out Button */}
          <button
            onClick={handleLogout}
            title="Sign out of Super Admin"
            className="p-2 rounded-xl bg-slate-900 hover:bg-rose-500/15 border border-slate-800 hover:border-rose-500/30 text-slate-400 hover:text-rose-400 transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Secondary Tab Navigation Bar */}
      <nav className="border-b border-slate-800/80 bg-slate-950/60 px-4 sm:px-8 overflow-x-auto scrollbar-none">
        <div className="flex items-center gap-1 sm:gap-2 min-w-max py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onSelectTab(item.id)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold tracking-wide transition-all ${
                  active
                    ? "bg-gradient-to-r from-teal-500/20 to-emerald-500/20 text-teal-300 border border-teal-500/40 shadow-inner"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-900/60 border border-transparent"
                }`}
              >
                <Icon className={`w-4 h-4 ${active ? "text-teal-400" : "text-slate-500"}`} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Main Content Area */}
      <main className="flex-1 p-4 sm:p-8 max-w-7xl w-full mx-auto">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-900 py-4 px-6 text-center text-xs text-slate-600">
        Rental Tracker Multi-Tenant Engine • Master Operations Portal
      </footer>

      {/* Sign Out Confirmation Modal */}
      <SignOutConfirmModal
        open={signOutModalOpen}
        onClose={() => !signingOut && setSignOutModalOpen(false)}
        onConfirm={handleConfirmLogout}
        userName={admin?.username || "root"}
        isSuperAdmin={true}
        signingOut={signingOut}
      />
    </div>
  );
}
